// src/utils/userAttributesHelper.js
import { fetchUserAttributes } from '@aws-amplify/auth';
import { syncUserAttributesToDynamoDB } from '../services/studentService';

/**
 * Fetches the current user's attributes including billing information
 * @returns {Object} User attributes with billing information
 */
export const getUserBillingInfo = async () => {
    try {
        const attributes = await fetchUserAttributes();
        
        // Combine first and last name for full name
        const firstName = attributes.given_name || '';
        const lastName = attributes.family_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        
        // Map Cognito attributes to billing info format
        const billingInfo = {
            customerName: fullName,
            customerEmail: attributes.email || '',
            phone: attributes.phone_number || '',
            // Use custom attributes for individual address components
            billingAddress1: attributes['custom:address1'] || '',
            billingAddress2: attributes['custom:address2'] || '',
            billingCity: attributes['custom:city'] || '',
            billingCounty: attributes['custom:county'] || '',
            billingPostcode: attributes['custom:postal_code'] || '',
            billingCountry: attributes['custom:country'] || 'IE',
            gender: attributes['custom:gender'] || '',
            userType: attributes['custom:user_type'] || 'student',
            
            // The formatted address string (if needed)
            formattedAddress: attributes.address || '',
            
            // Keep original attributes for reference
            cognitoAttributes: attributes
        };
        
        return billingInfo;
    } catch (error) {
        console.error('Error fetching user attributes:', error);
        return null;
    }
};

/**
 * Formats billing info for display
 * @param {Object} billingInfo - The billing information object
 * @returns {Object} Formatted billing info
 */
export const formatBillingInfo = (billingInfo) => {
    if (!billingInfo) return null;
    
    return {
        fullAddress: [
            billingInfo.billingAddress1,
            billingInfo.billingAddress2,
            billingInfo.billingCity,
            billingInfo.billingCounty,
            billingInfo.billingPostcode,
            billingInfo.billingCountry
        ].filter(Boolean).join('\n'),
        
        shortAddress: `${billingInfo.billingCity}, ${billingInfo.billingCounty ? billingInfo.billingCounty + ', ' : ''}${billingInfo.billingCountry}`,
        
        isComplete: !!(
            billingInfo.customerName &&
            billingInfo.billingAddress1 &&
            billingInfo.billingCity &&
            billingInfo.billingPostcode
        )
    };
};

/**
 * Sync user attributes from Cognito to DynamoDB
 * This ensures gender and user_type are stored in both places
 * @returns {Promise} Result of sync operation
 */
export const syncUserAttributes = async () => {
    try {
        const attributes = await fetchUserAttributes();
        
        // Only sync if we have the custom attributes
        if (attributes['custom:gender'] || attributes['custom:user_type']) {
            return await syncUserAttributesToDynamoDB(attributes);
        }
        
        return null;
    } catch (error) {
        console.error('Error syncing user attributes:', error);
        // Don't throw - this is a non-critical operation
        return null;
    }
};